import { useState, useEffect } from 'react';
import { getJyotirlingas, markJyotirlingaViewed } from '../services/api';
import { triggerToast } from '../components/Toast';
import ImageWithFallback from '../components/ImageWithFallback';
import JyotirlIngaIllustration from '../components/JyotirlIngaIllustration';
import BackButton from '../components/BackButton';
import { OmIcon, TrishulIcon, LockIcon } from '../components/SacredIcons';
import { useLang, localizeJyotirlinga, TOTAL_JYOTIRLINGAS } from '../i18n';

function formatRam(n) {
  if (n >= 100000) return (n / 100000).toFixed(1).replace(/\.0$/, '') + ' लाख';
  if (n >= 1000)   return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

function JyotirlIngaModal({ j, onClose }) {
  const { t } = useLang();
  const locked = !j.is_unlocked;

  return (
    <div
      className="peeth-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="peeth-modal" onClick={e => e.stopPropagation()}>
        {/* Hero */}
        {j.image_url ? (
          <img
            src={j.image_url}
            alt={j.name}
            className="peeth-modal-hero"
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="illus-hero-wrap">
            <JyotirlIngaIllustration
              jyotirlingaId={j.jyotirlinga_id}
              state={j.state}
            />
            <div className="illus-hero-gradient"
              style={{ background: 'linear-gradient(transparent, #FFF5E8)' }} />
          </div>
        )}

        <button className="peeth-modal-close" onClick={onClose} aria-label={t('common.close')}>✕</button>

        <div style={{ padding: '16px 20px 28px' }}>
          {/* Lock notice */}
          {locked && (
            <div style={{
              background: 'rgba(240,225,255,0.65)',
              border: '1px solid rgba(150,80,200,0.22)',
              borderRadius: 12, padding: '10px 16px',
              marginBottom: 16, textAlign: 'center',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              <LockIcon size={16} color="#7B00C8" />
              <span style={{ fontFamily: 'var(--font-hindi)', fontSize: 14, color: 'var(--text-dark)' }}>
                {t('jyoti.unlockAt', { ram: formatRam(j.ram_count_to_unlock) })}
              </span>
            </div>
          )}

          <div className="peeth-modal-number">{t('jyoti.modalNumber', { id: j.jyotirlinga_id })}</div>
          <div className="peeth-modal-name">{j.name}</div>
          {j.temple_name && (
            <div className="peeth-modal-temple">{j.temple_name}</div>
          )}

          {/* Info Grid */}
          <div className="peeth-info-grid" style={{ marginTop: 16, marginBottom: 16 }}>
            {[
              { label: t('info.location'),       value: j.location },
              { label: t('info.state'),          value: j.state },
              { label: t('info.country'),        value: j.country || 'India' },
              { label: t('jyoti.infoShivaForm'), value: j.shiva_form },
            ].filter(i => i.value).map(item => (
              <div key={item.label} className="peeth-info-item">
                <div className="peeth-info-label">{item.label}</div>
                <div className="peeth-info-value">{item.value}</div>
              </div>
            ))}
          </div>

          {/* History */}
          {j.history && (
            <div className="peeth-section">
              <div className="peeth-section-title"><OmIcon /> <span>{t('section.history')}</span></div>
              <div className="peeth-section-text">{j.history}</div>
            </div>
          )}

          {/* Significance */}
          {j.significance && (
            <div className="peeth-section">
              <div className="peeth-section-title"><TrishulIcon /> <span>{t('section.significance')}</span></div>
              <div className="peeth-section-text">{j.significance}</div>
            </div>
          )}

          {/* Ram count */}
          <div style={{
            textAlign: 'center', padding: '10px', marginTop: 8,
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 10, border: '1px solid rgba(212,175,55,0.1)',
          }}>
            <span style={{ fontFamily: 'var(--font-hindi)', fontSize: 13, color: 'var(--text-soft)', opacity: 0.85, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <LockIcon size={14} open={j.is_unlocked} />
              {j.is_unlocked
                ? t('jyoti.ramUnlocked', { ram: formatRam(j.ram_count_to_unlock) })
                : t('jyoti.ramWillUnlock', { ram: formatRam(j.ram_count_to_unlock) })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function JyotirlingsScreen({ user, isGuest, onBack }) {
  const { lang, t } = useLang();
  const [jyotirlingas, setJyotirlingas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    async function load() {
      try {
        const data = await getJyotirlingas();
        setJyotirlingas(data.jyotirlingas || []);
      } catch {
        triggerToast(t('jyoti.loadError'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSelect(j) {
    // Locked Jyotirlingas stay closed — reveal nothing until the Ram-count
    // threshold is reached. Give gentle feedback instead of opening the card.
    if (!j.is_unlocked) {
      triggerToast(t('jyoti.unlockAt', { ram: formatRam(j.ram_count_to_unlock) }));
      return;
    }
    setSelected(j);
    if (!j.viewed && !isGuest) {
      try {
        await markJyotirlingaViewed(j.jyotirlinga_id);
        setJyotirlingas(prev => prev.map(item =>
          item.jyotirlinga_id === j.jyotirlinga_id ? { ...item, viewed: true } : item
        ));
      } catch {
        // non-critical
      }
    }
  }

  const localized = jyotirlingas.map(j => localizeJyotirlinga(j, lang));
  const unlocked  = localized.filter(j => j.is_unlocked).length;
  const total     = localized.length || TOTAL_JYOTIRLINGAS;

  const filtered = filter === 'unlocked'
    ? localized.filter(j => j.is_unlocked)
    : filter === 'locked'
    ? localized.filter(j => !j.is_unlocked)
    : localized;

  return (
    <div className="screen" style={{ padding: 0, overflow: 'hidden', height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="screen-header">
        <BackButton onClick={onBack} />
        <div style={{ flex: 1 }}>
          <div className="screen-header-title">{t('jyoti.title', { total })}</div>
          <div className="screen-header-sub">{t('jyoti.unlockedCount', { n: unlocked, total })}</div>
        </div>
        <div style={{
          background: 'rgba(150,80,200,0.12)',
          border: '1px solid rgba(150,80,200,0.30)',
          borderRadius: 10, padding: '4px 10px',
          fontFamily: 'var(--font-display)', fontSize: 12,
          color: '#7B00C8',
        }}>
          {Math.round((unlocked / total) * 100)}%
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar-wrap">
        <div className="progress-bar-bg">
          <div className="progress-bar-fill" style={{
            width: `${(unlocked / total) * 100}%`,
            background: 'linear-gradient(90deg, #9b59b6, #d4af37)',
          }} />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs-wrap">
        {[
          { id: 'all',      label: t('jyoti.filterAll') },
          { id: 'unlocked', label: t('jyoti.filterUnlocked', { n: unlocked }) },
          { id: 'locked',   label: t('jyoti.filterLocked', { n: total - unlocked }) },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            style={{
              padding: '6px 14px', borderRadius: 20,
              border: filter === tab.id
                ? '1.5px solid rgba(150,80,200,0.50)'
                : '1px solid rgba(150,80,200,0.18)',
              background: filter === tab.id
                ? 'linear-gradient(135deg, rgba(130,50,180,0.18), rgba(100,30,150,0.25))'
                : 'rgba(248,240,255,0.5)',
              color: filter === tab.id ? '#6B00B0' : 'var(--text-soft)',
              fontFamily: 'var(--font-hindi)', fontSize: 13,
              cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        // Mirror the real .peeth-grid + card shape so the skeleton reads as the
        // actual cards loading in (same columns, gap, aspect, radius).
        <div className="peeth-grid" style={{ flex: 1 }}>
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="skeleton" style={{ aspectRatio: '3/4', minHeight: 200, borderRadius: 16 }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <OmIcon size={48} color="var(--gold)" />
          <p style={{ fontFamily: 'var(--font-hindi)', fontSize: 18, color: 'var(--cream)', opacity: 0.6 }}>
            {filter === 'unlocked' ? t('jyoti.emptyUnlocked') : t('jyoti.emptyAll')}
          </p>
        </div>
      ) : (
        <div className="peeth-grid" style={{ flex: 1 }}>
          {filtered.map((j, idx) => (
            <div
              key={j.jyotirlinga_id}
              className={`peeth-card ${j.is_unlocked ? 'peeth-card-unlocked' : 'peeth-card-locked'}`}
              style={{ '--i': idx }}
              onClick={() => handleSelect(j)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && handleSelect(j)}
            >
              {j.image_url ? (
                <ImageWithFallback
                  src={j.image_url}
                  alt={j.name}
                  className="peeth-card-img"
                  fallbackGradient="linear-gradient(135deg, #0D0825, #1a0030)"
                />
              ) : (
                <div className="peeth-card-img" style={{ overflow:'hidden', borderRadius:'inherit' }}>
                  <JyotirlIngaIllustration
                    jyotirlingaId={j.jyotirlinga_id}
                    state={j.state}
                  />
                </div>
              )}
              <div className="peeth-card-overlay">
                <div className="peeth-card-number">#{j.jyotirlinga_id}</div>
                <div className="peeth-card-name">{j.name}</div>
                <div className="peeth-card-location">{j.state}</div>
              </div>
              {!j.is_unlocked && (
                <div className="peeth-lock-badge">
                  <LockIcon size={13} style={{ marginRight: 4 }} />{formatRam(j.ram_count_to_unlock)}
                </div>
              )}
              {j.is_unlocked && !j.viewed && (
                <div style={{
                  position: 'absolute', top: 8, right: 8,
                  width: 10, height: 10, borderRadius: '50%',
                  background: '#9b59b6',
                  boxShadow: '0 0 6px rgba(155,89,182,0.7)',
                }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <JyotirlIngaModal j={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
